import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { createPortal } from 'react-dom';
import {
  AssistantTodoItem,
  GetAllAssistantTodosResponse,
  SubThreadCreatedEvent,
  SubThreadUpdateEvent,
  AgentStreamEvents,
  AgentEvents,
  SubThreadStatus,
  ResponseArtifactDTO,
  TodoImageReadyEvent,
} from '@dreamai/types';
import { usePusher } from '../../../contexts/PusherContext';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../../api';
import TasksSkeleton from './TasksSkeleton';
import ErrorState from '../../common/ErrorState';
import ResearchBlockModal from '../canvas/ResearchBlockModal';
import NewThreadModal, {
  type CreationType,
} from '../ExpansiveSideNavigation/NewThreadModal';
import { ProjectAppLoadingProvider } from '../../../providers/ProjectAppViewLoadingProvider';
import { ReactComponent as TodoSpinnerIcon } from '../../../icons/todo-spinner.svg';
import { ReactComponent as PlusAddIcon } from '../../../icons/plus-add.svg';
import { ReactComponent as AudioWavesIcon } from '../../../icons/audio-waves.svg';
import { ReactComponent as ClockIcon } from '../../../icons/clock.svg';
import { ReactComponent as HourglassIcon } from '../../../icons/hourglass.svg';
import { ReactComponent as AlertSquareIcon } from '../../../icons/alert-square.svg';
import { ReactComponent as DocumentTextIcon } from '../../../icons/document-text.svg';
import { ReactComponent as EmailIcon } from '../../../icons/email.svg';
import { ReactComponent as ChatBubbleIcon } from '../../../icons/chat-bubble.svg';

import { ReactComponent as CloseXIcon } from '../../../icons/close-x.svg';
import { ReactComponent as ClipboardCheckIcon } from '../../../icons/clipboard-check.svg';
import { useOnboardingModals } from '../../../providers';
import { useThreadReads } from '../../../providers/ThreadReadsProvider';
import { useMobileNav } from '../../../contexts/MobileNavContext';
import DismissTodoModal from './DismissTodoModal';
import './agent-tasks-list.scss';

// ─── Helpers ──────────────────────────────────────────────────

const getGroupForTodo = (todo: {
  isArchivedThread?: boolean;
}): 'active' | 'done' => {
  return todo.isArchivedThread ? 'done' : 'active';
};

const getStatusPriority = (status: string): number => {
  switch (status) {
    case 'waiting_on_user':
      return 0;
    case 'processing':
      return 1;
    case 'waiting_on_third_party':
      return 2;
    default:
      return 3;
  }
};

const localizeTimestamp = (iso: string): string => {
  const target = new Date(iso);
  if (isNaN(target.getTime())) return iso;
  const now = new Date();
  const diffMs = target.getTime() - now.getTime();
  const absMs = Math.abs(diffMs);
  const timeStr = target.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
  if (diffMs > 0 && absMs < 24 * 60 * 60 * 1000) return timeStr;
  if (diffMs > 0 && absMs < 7 * 24 * 60 * 60 * 1000) {
    const dayName = target.toLocaleDateString(undefined, { weekday: 'long' });
    return `${dayName} at ${timeStr}`;
  }
  return (
    target.toLocaleDateString(undefined, { day: 'numeric', month: 'long' }) +
    ` at ${timeStr}`
  );
};

const formatEta = (eta: string): string | null => {
  if (eta.includes('{timestamp}')) {
    return eta.replace(/\{timestamp\}([^{]+)\{\/timestamp\}/g, (_, iso) =>
      localizeTimestamp(iso.trim()),
    );
  }
  const target = new Date(eta);
  if (!isNaN(target.getTime())) return localizeTimestamp(eta);
  return eta;
};

const getActiveStep = (todo: AssistantTodoItem) => {
  const steps = todo.steps || [];
  return (
    steps.find((s) => s.active) ||
    steps.find((s) => s.status === 'in_progress') ||
    steps.find((s) => s.status === 'waiting_on_user') ||
    steps.find((s) => s.status === 'waiting_on_third_party') ||
    steps.find((s) => s.status === 'failed') ||
    steps.find((s) => s.status === 'pending')
  );
};

const resolveDisplayStatus = (
  todo: AssistantTodoItem,
  activeStep?: { status: string },
): 'complete' | 'failed' | 'waiting' | 'processing' => {
  const stepStatus = activeStep?.status || todo.status;
  if (stepStatus === 'complete') return 'complete';
  if (stepStatus === 'failed' || stepStatus === 'cancelled_by_user')
    return 'failed';
  if (
    stepStatus === 'waiting_on_user' ||
    stepStatus === 'waiting_on_third_party'
  )
    return 'waiting';
  return 'processing';
};

const getStepIcon = (displayStatus: string) => {
  if (displayStatus === 'complete')
    return (
      <svg
        className='step-icon complete'
        xmlns='http://www.w3.org/2000/svg'
        width='16'
        height='16'
        viewBox='0 0 16 16'
        fill='none'
      >
        <circle cx='8' cy='8' r='6' stroke='#489FD9' strokeWidth='4' />
      </svg>
    );
  if (displayStatus === 'failed')
    return <AlertSquareIcon className='step-icon failed' />;
  if (displayStatus === 'waiting')
    return <HourglassIcon className='step-icon hourglass' />;
  return <TodoSpinnerIcon className='step-icon spinner' />;
};

/** Map displayStatus → indicator bar colour class */
const getIndicatorClass = (
  displayStatus: 'complete' | 'failed' | 'waiting' | 'processing',
): string => {
  if (displayStatus === 'complete') return 'indicator-complete';
  if (displayStatus === 'failed') return 'indicator-failed';
  return 'indicator-active';
};

// ─── Types ────────────────────────────────────────────────────

interface PendingTodoCard {
  id: string;
  description: string;
  tempTodoId?: string;
}

type SectionTab = 'active' | 'archived';

interface AgentTasksListProps {
  agentNickname: string;
  onNavigateToThread?: (
    threadId: string,
    parentThreadId?: string,
    researchAppId?: string,
  ) => void;
  onAddTodo?: () => void;
  pendingTodo?: PendingTodoCard | null;
  threadId?: string | null;
  agentId?: string;
  agentImageUrl?: string;
  agentType?: string;
  onCall?: () => void;
  onAsk?: () => void;
  onOverlayChange?: (isOpen: boolean) => void;
  onSectionTabChange?: (tab: SectionTab) => void;
  dailyStandupId?: string | null;
  countdown?: string;
  onRundown?: () => void;
  hideFab?: boolean;
}

export interface AgentTasksListHandle {
  refresh: () => Promise<void>;
}

// ─── Component ────────────────────────────────────────────────

const AgentTasksList = forwardRef<AgentTasksListHandle, AgentTasksListProps>(
  function AgentTasksList(
    {
      agentNickname,
      onNavigateToThread,
      onAddTodo,
      pendingTodo,
      threadId,
      agentId,
      agentImageUrl,
      agentType,
      onCall,
      onAsk,
      onOverlayChange,
      onSectionTabChange,
      dailyStandupId,
      countdown,
      onRundown,
      hideFab,
    },
    ref,
  ) {
    const { getAccessTokenSilently } = useAuth0();
    const { hasSeenModal } = useOnboardingModals();
    const { getUnreadThreadIdsByAgent } = useThreadReads();
    const { isMobile } = useMobileNav();
    const pusherCtx = usePusher(true) as any;
    const pusherChannel = pusherCtx?.pusherChannel ?? null;
    const userChannel = pusherCtx?.userChannel ?? null;

    // Stop-task confirmation modal
    const [stopModalTodo, setStopModalTodo] =
      useState<AssistantTodoItem | null>(null);

    // Section tabs (header-level): just active vs archived
    const [sectionTab, setSectionTabInternal] = useState<SectionTab>('active');
    const setSectionTab = useCallback(
      (tab: SectionTab) => {
        setSectionTabInternal(tab);
        onSectionTabChange?.(tab);
      },
      [onSectionTabChange],
    );

    // Mobile FAB overlay
    const [fabOpen, setFabOpen] = useState(false);

    // Data
    const [todos, setTodos] = useState<AssistantTodoItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Animation
    const [animatingItems, setAnimatingItems] = useState<Set<string>>(
      new Set(),
    );
    const [completingItems, setCompletingItems] = useState<Set<string>>(
      new Set(),
    );
    const completingItemsRef = useRef(completingItems);
    completingItemsRef.current = completingItems;

    // Ephemeral loading cards for newly-created agent tasks (client-side only)
    const [pendingTodos, setPendingTodos] = useState<PendingTodoCard[]>([]);
    const pendingTodosRef = useRef(pendingTodos);
    pendingTodosRef.current = pendingTodos;
    // IDs that were already removed by the Pusher handler — prevents the
    // useEffect below from re-adding them on subsequent renders.
    const removedPendingIdsRef = useRef<Set<string>>(new Set());

    useEffect(() => {
      if (!pendingTodo) return;
      if (removedPendingIdsRef.current.has(pendingTodo.id)) return;
      setPendingTodos((prev) => {
        // If a card with this id already exists, update its tempTodoId
        const existing = prev.find((t) => t.id === pendingTodo.id);
        if (existing) {
          if (existing.tempTodoId === pendingTodo.tempTodoId) return prev;
          return prev.map((t) =>
            t.id === pendingTodo.id
              ? { ...t, tempTodoId: pendingTodo.tempTodoId }
              : t,
          );
        }
        return [pendingTodo, ...prev];
      });
    }, [pendingTodo]);

    // Defensive cleanup: remove stale pending cards once real todos arrive.
    // If the pending card id (tempTodoId) hasn't been removed by the Pusher
    // handler yet but a real todo already exists, drop the card.
    useEffect(() => {
      if (pendingTodos.length === 0 || todos.length === 0) return;
      setPendingTodos((prev) => {
        const next = prev.filter((pt) => {
          const pDesc = pt.description?.toLowerCase().trim();
          const matched = todos.some((t) => {
            const tDesc = t.description?.toLowerCase().trim();
            return tDesc === pDesc;
          });
          if (matched) removedPendingIdsRef.current.add(pt.id);
          return !matched;
        });
        return next.length === prev.length ? prev : next;
      });
    }, [pendingTodos, todos]);

    // Dropdown menu (removed — replaced by inline action buttons)

    // Research overlay
    const [linkedResearchAppId, setLinkedResearchAppId] = useState<
      string | null
    >(null);

    // Create project/autopilot modal
    const [createModalType, setCreateModalType] = useState<CreationType | null>(
      null,
    );

    // ─── Data fetching ────────────────────────────────────────

    const fetchData = useCallback(
      async ({ silent = false }: { silent?: boolean } = {}) => {
        if (!silent) setLoading(true);
        setError(null);
        try {
          const token = await getAccessTokenSilently();
          const parentThreadQs = threadId ? `?parentThreadId=${threadId}` : '';
          const agentQs = agentId
            ? parentThreadQs
              ? `&agentId=${agentId}`
              : `?agentId=${agentId}`
            : '';

          const authHeaders = {
            headers: { Authorization: `Bearer ${token}` },
          };
          const todosRes = await api.get<GetAllAssistantTodosResponse>(
            `/todos${parentThreadQs}${agentQs}`,
            authHeaders,
          );

          setTodos(todosRes.data.todos);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Unknown error');
        } finally {
          if (!silent) setLoading(false);
        }
      },
      [getAccessTokenSilently, threadId, agentId],
    );

    useEffect(() => {
      fetchData();
    }, [fetchData]);

    useImperativeHandle(
      ref,
      () => ({
        refresh: () => fetchData({ silent: true }),
      }),
      [fetchData],
    );

    // ─── Realtime ─────────────────────────────────────────────

    useEffect(() => {
      if (!pusherChannel) return;

      const handleSubThreadCreated = (data: SubThreadCreatedEvent) => {
        if (!data.subThread) return;
        if (threadId && data.parentThreadId !== threadId) return;
        if (agentId && data.parentAgentId && data.parentAgentId !== agentId)
          return;
        if (data.subThread.isInternalWork) return;
        if (data.notVisibleInTaskList) return;

        const newTodo: AssistantTodoItem = {
          id: data.subThread.id,
          description: data.subThread.description,
          status: data.subThread.status as SubThreadStatus,
          summary: data.subThread.summary,
          completionEta: data.subThread.completionEta,
          steps: data.subThread.steps,
          createdAt: data.subThread.createdAt,
          updatedAt: data.subThread.updatedAt,
          parentThreadId: data.parentThreadId,
          parentThreadDescription: data.parentThreadDescription || 'Thread',
          parentThreadIsMainThread: data.parentThreadIsMainThread,
          parentThreadIsInternalMonologue: data.parentThreadIsInternalMonologue,
          parentThreadIsResponseArtifactThread:
            data.parentThreadIsResponseArtifactThread,
          parentThreadIsRehomedTask: data.parentThreadIsRehomedTask,
          parentThreadIsWhatsappThread: data.parentThreadIsWhatsappThread,
          informationDumps: data.subThread.informationDumps,
          iconImageUrl: data.subThread.iconImageUrl,
          gmailLink: data.subThread.gmailLink,
          latestArtifact: (data.subThread as AssistantTodoItem).latestArtifact,
        };

        // Remove the pending loading card that this sub-thread replaces.
        // Match by tempTodoId when available, fall back to removing oldest.
        setPendingTodos((prev) => {
          if (prev.length === 0) return prev;
          let removeIdx = -1;
          if (data.replacesTempTodoId) {
            removeIdx = prev.findIndex(
              (t) => t.tempTodoId === data.replacesTempTodoId,
            );
          }
          // Fallback — remove oldest (last in array since we prepend)
          if (removeIdx < 0) removeIdx = prev.length - 1;
          // Record the removed id so the useEffect doesn't re-add it
          removedPendingIdsRef.current.add(prev[removeIdx].id);
          return prev.filter((_, i) => i !== removeIdx);
        });

        setTodos((prev) => {
          if (prev.some((t) => t.id === newTodo.id)) return prev;
          return [newTodo, ...prev];
        });

        setAnimatingItems((prev) => new Set(prev).add(newTodo.id));
        setTimeout(() => {
          setAnimatingItems((prev) => {
            const next = new Set(prev);
            next.delete(newTodo.id);
            return next;
          });
        }, 400);
      };

      const handleSubThreadUpdate = (data: SubThreadUpdateEvent) => {
        if (!data.subThread) return;
        if (threadId && data.parentThreadId !== threadId) return;
        if (agentId && data.parentAgentId && data.parentAgentId !== agentId)
          return;
        if (data.subThread.isInternalWork) return;
        if (data.notVisibleInTaskList) return;
        if (completingItemsRef.current.has(data.subThread.id)) return;

        setTodos((prev) => {
          const idx = prev.findIndex((t) => t.id === data.subThread.id);
          if (idx === -1) {
            const newTodo: AssistantTodoItem = {
              id: data.subThread.id,
              description: data.subThread.description,
              status: data.subThread.status as SubThreadStatus,
              summary: data.subThread.summary,
              completionEta: data.subThread.completionEta,
              steps: data.subThread.steps,
              createdAt: data.subThread.createdAt,
              updatedAt: data.subThread.updatedAt,
              parentThreadId: data.parentThreadId,
              parentThreadDescription: data.parentThreadDescription || 'Thread',
              parentThreadIsInternalMonologue:
                data.parentThreadIsInternalMonologue,
              parentThreadIsResponseArtifactThread:
                data.parentThreadIsResponseArtifactThread,
              parentThreadIsRehomedTask: data.parentThreadIsRehomedTask,
              parentThreadIsWhatsappThread: data.parentThreadIsWhatsappThread,
              latestArtifact: (data.subThread as AssistantTodoItem)
                .latestArtifact,
            };
            return [newTodo, ...prev];
          }
          const updated = [...prev];
          updated[idx] = {
            ...prev[idx],
            status: data.subThread.status as SubThreadStatus,
            summary: data.subThread.summary,
            completionEta: data.subThread.completionEta,
            steps: data.subThread.steps,
            updatedAt: data.subThread.updatedAt,
            informationDumps: data.subThread.informationDumps,
            iconImageUrl: data.subThread.iconImageUrl ?? prev[idx].iconImageUrl,
            gmailLink: data.subThread.gmailLink ?? prev[idx].gmailLink,
            latestArtifact: (data.subThread as AssistantTodoItem)
              .latestArtifact,
            isArchivedThread:
              (data.subThread as AssistantTodoItem).isArchivedThread ??
              prev[idx].isArchivedThread,
          };
          return updated;
        });
      };

      pusherChannel.bind(
        AgentStreamEvents.SUB_THREAD_CREATED,
        handleSubThreadCreated,
      );
      pusherChannel.bind(
        AgentStreamEvents.SUB_THREAD_UPDATE,
        handleSubThreadUpdate,
      );

      return () => {
        pusherChannel.unbind(
          AgentStreamEvents.SUB_THREAD_CREATED,
          handleSubThreadCreated,
        );
        pusherChannel.unbind(
          AgentStreamEvents.SUB_THREAD_UPDATE,
          handleSubThreadUpdate,
        );
      };
    }, [pusherChannel, threadId]);

    // Listen for icon image ready on the user channel
    useEffect(() => {
      if (!userChannel) return;

      const handleTodoImageReady = (data: TodoImageReadyEvent) => {
        if (!data.imageUrl || !data.description) return;
        setTodos((prev) =>
          prev.map((t) =>
            t.description === data.description && !t.iconImageUrl
              ? { ...t, iconImageUrl: data.imageUrl }
              : t,
          ),
        );
      };

      userChannel.bind(AgentEvents.TODO_IMAGE_READY, handleTodoImageReady);
      return () => {
        userChannel.unbind(AgentEvents.TODO_IMAGE_READY, handleTodoImageReady);
      };
    }, [userChannel]);

    // ─── Actions ──────────────────────────────────────────────

    const handleNavigateToThread = useCallback(
      (todo: AssistantTodoItem) => {
        onNavigateToThread?.(todo.id, todo.parentThreadId);
      },
      [onNavigateToThread],
    );

    const handleStopClick = useCallback(
      (todo: AssistantTodoItem) => {
        if (hasSeenModal('dismiss_todo_warning')) {
          handleStopTask(todo);
        } else {
          setStopModalTodo(todo);
        }
      },
      [hasSeenModal],
    );

    const handleStopTask = useCallback(
      async (todo: AssistantTodoItem) => {
        try {
          const token = await getAccessTokenSilently();
          await api.post(
            `/todos/${todo.id}/cancel`,
            {},
            { headers: { Authorization: `Bearer ${token}` } },
          );
          setTodos((prev) =>
            prev.map((t) =>
              t.id === todo.id
                ? {
                    ...t,
                    status: 'cancelled_by_user' as SubThreadStatus,
                    summary: 'Cancelled by user',
                  }
                : t,
            ),
          );
        } catch (err) {
          console.error('Error stopping task:', err);
        }
      },
      [getAccessTokenSilently],
    );

    const handleHideTask = useCallback(
      async (todo: AssistantTodoItem) => {
        try {
          const token = await getAccessTokenSilently();
          await api.post(
            `/todos/${todo.id}/archive`,
            {},
            { headers: { Authorization: `Bearer ${token}` } },
          );
          setTodos((prev) =>
            prev.map((t) =>
              t.id === todo.id ? { ...t, isArchivedThread: true } : t,
            ),
          );
        } catch (err) {
          console.error('Error hiding task:', err);
        }
      },
      [getAccessTokenSilently],
    );

    // ─── Grouped + sorted ────────────────────────────────────

    const { activeTodos, doneTodos } = useMemo(() => {
      const active: AssistantTodoItem[] = [];
      const done: AssistantTodoItem[] = [];

      todos.forEach((todo) => {
        // Only show standalone tasks (no parent) or tasks whose parent is the
        // agent's internal monologue, a RehomedTask holder, a QuickChat
        // TaskParent holder, or a WhatsApp thread. Skip tasks that belong to
        // a real project, autopilot, or campaign (those have a non-holder parent thread).
        const isStandalone =
          !todo.parentThreadId ||
          todo.parentThreadIsInternalMonologue === true ||
          todo.parentThreadIsRehomedTask === true ||
          todo.parentThreadIsTaskParent === true ||
          todo.parentThreadIsWhatsappThread === true;
        if (!isStandalone) return;

        if (todo.isArchivedThread) {
          done.push(todo);
        } else {
          active.push(todo);
        }
      });

      active.sort((a, b) => {
        const aDisplay = resolveDisplayStatus(a, getActiveStep(a));
        const bDisplay = resolveDisplayStatus(b, getActiveStep(b));
        const aComplete = aDisplay === 'complete';
        const bComplete = bDisplay === 'complete';
        if (aComplete !== bComplete) return aComplete ? 1 : -1;
        return (
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
      });

      done.sort((a, b) => {
        const aTime = a.updatedAt || a.createdAt;
        const bTime = b.updatedAt || b.createdAt;
        return new Date(bTime).getTime() - new Date(aTime).getTime();
      });

      return { activeTodos: active, doneTodos: done };
    }, [todos]);

    const displayedTodos = sectionTab === 'active' ? activeTodos : doneTodos;

    // ─── Unread tracking ───────────────────────────────────────

    const unreadThreadIds = useMemo(() => {
      if (!agentId) return new Set<string>();
      return new Set(getUnreadThreadIdsByAgent(agentId));
    }, [agentId, getUnreadThreadIdsByAgent]);

    const hasUnreadTasks = useMemo(
      () => todos.some((t) => unreadThreadIds.has(t.id)),
      [todos, unreadThreadIds],
    );

    // ─── Render item ──────────────────────────────────────────

    const renderItem = (todo: AssistantTodoItem) => {
      const isAnimating = animatingItems.has(todo.id);
      const isCompleting = completingItems.has(todo.id);
      const activeStep = getActiveStep(todo);
      const displayStatus = resolveDisplayStatus(todo, activeStep);
      const isTerminalStatus =
        displayStatus === 'complete' || displayStatus === 'failed';
      const stepText = isTerminalStatus
        ? todo.summary || activeStep?.description || 'Done'
        : activeStep?.description || todo.summary || 'Working on it...';
      const etaText = todo.completionEta ? formatEta(todo.completionEta) : null;
      const indicatorClass = getIndicatorClass(displayStatus);

      return (
        <div
          key={todo.id}
          className={`agent-task-item ${displayStatus}${isAnimating ? ' animate-in' : ''}${isCompleting ? ' completing' : ''}`}
          data-agent-task-id={todo.id}
          onClick={() => handleNavigateToThread(todo)}
          role='button'
          tabIndex={0}
        >
          <div className={`indicator ${indicatorClass}`} />

          <div className='task-icon'>
            {todo.iconImageUrl ? (
              <img src={todo.iconImageUrl} alt='' />
            ) : (
              <div className='task-icon-placeholder' />
            )}
          </div>

          <div className='task-main'>
            {unreadThreadIds.has(todo.id) && (
              <div className='unread-tag'>
                <span className='unread-dot' />
                Unread
              </div>
            )}
            <div className='task-description'>{todo.description}</div>
            <div className='task-status'>
              {getStepIcon(displayStatus)}
              <span>{stepText}</span>
            </div>
            {!isTerminalStatus && etaText && (
              <div className='task-eta'>
                <ClockIcon className='clock-icon' />
                <span>{etaText}</span>
              </div>
            )}
            {todo.informationDumps && todo.informationDumps.length > 0 && (
              <div className='task-research'>
                {todo.informationDumps.map((dump) =>
                  dump.projectAppId ? (
                    <div
                      key={dump.id}
                      className='research-link'
                      onClick={(e) => {
                        e.stopPropagation();
                        onNavigateToThread?.(
                          todo.id,
                          todo.parentThreadId,
                          dump.projectAppId!,
                        );
                      }}
                      role='button'
                      tabIndex={0}
                    >
                      <DocumentTextIcon className='document-icon' />
                      <span>{dump.title || 'View Research'}</span>
                    </div>
                  ) : null,
                )}
              </div>
            )}
            {todo.gmailLink && (
              <div className='task-research'>
                <a
                  className='research-link gmail-link'
                  href={todo.gmailLink}
                  target='_blank'
                  rel='noopener noreferrer'
                  onClick={(e) => e.stopPropagation()}
                >
                  <EmailIcon className='document-icon' />
                  <span>View Draft in Gmail</span>
                </a>
              </div>
            )}
          </div>

          <div className='task-actions'>
            {!isTerminalStatus ? (
              <button
                className='action-btn stop-btn'
                title='Stop task'
                onClick={(e) => {
                  e.stopPropagation();
                  handleStopClick(todo);
                }}
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='20'
                  height='20'
                  viewBox='0 0 22 22'
                  fill='none'
                >
                  <path
                    d='M7 11H15M21 11C21 16.5228 16.5228 21 11 21C5.47715 21 1 16.5228 1 11C1 5.47715 5.47715 1 11 1C16.5228 1 21 5.47715 21 11Z'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </button>
            ) : !todo.isArchivedThread ? (
              <button
                className='action-btn archive-btn'
                title='Archive'
                onClick={(e) => {
                  e.stopPropagation();
                  handleHideTask(todo);
                }}
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='20'
                  height='20'
                  viewBox='0 0 22 22'
                  fill='none'
                >
                  <path
                    d='M8 8L14 14M14 8L8 14M21 11C21 16.5228 16.5228 21 11 21C5.47715 21 1 16.5228 1 11C1 5.47715 5.47715 1 11 1C16.5228 1 21 5.47715 21 11Z'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </button>
            ) : null}
          </div>
        </div>
      );
    };

    // ─── Render ───────────────────────────────────────────────

    if (loading) {
      return (
        <div className='agent-tasks-list'>
          <TasksSkeleton />
        </div>
      );
    }

    if (error) {
      return (
        <div className='agent-tasks-list'>
          <ErrorState title='Failed to load tasks' subtitle={error} />
        </div>
      );
    }

    return (
      <div className='agent-tasks-list'>
        <div className='header'>
          <button
            className={`header-tab${sectionTab === 'active' ? ' selected' : ''}`}
            onClick={() => setSectionTab('active')}
          >
            {agentNickname}&rsquo;s Tasks
            {hasUnreadTasks && <span className='header-tab-dot' />}
          </button>
          {!isMobile && (
            <button
              className={`header-tab${sectionTab === 'archived' ? ' selected' : ''}`}
              onClick={() => setSectionTab('archived')}
            >
              Archived
            </button>
          )}
        </div>
        {!isMobile && (onCall || onAsk || onAddTodo) && (
          <div className='header-pill'>
            {onCall && (
              <button
                className='pill-call-btn'
                onClick={onCall}
                aria-label={`Call ${agentNickname}`}
              >
                <div className='pill-avatar-wrap'>
                  {agentImageUrl && (
                    <img src={agentImageUrl} alt={agentNickname} />
                  )}
                </div>
                <AudioWavesIcon className='pill-audio-icon' />
              </button>
            )}
            {onAsk && (
              <button
                className='pill-ask-btn'
                onClick={onAsk}
                aria-label={`Ask ${agentNickname}`}
              >
                <ChatBubbleIcon />
              </button>
            )}
            {onAddTodo && (
              <button
                className='pill-plus-btn'
                onClick={() => onAddTodo?.()}
                aria-label='Add task'
              >
                <PlusAddIcon />
              </button>
            )}
          </div>
        )}

        <div className='tasks-container'>
          {displayedTodos.length === 0 && pendingTodos.length === 0 ? (
            <div className='empty-state'>
              <img
                className='empty-state-img'
                src='/splicons/holidaytask.png'
                alt=''
              />
              <div className='empty-state-text'>
                <div className='empty-state-main'>
                  {sectionTab === 'active'
                    ? "I'm not working on anything right now"
                    : 'No archived tasks'}
                </div>
                {sectionTab === 'active' && (
                  <div className='empty-state-minor'>
                    Let&rsquo;s start a call and plan your week
                  </div>
                )}
              </div>
              {sectionTab === 'active' && (
                <div className='empty-state-actions'>
                  {onCall && (
                    <button className='empty-state-btn' onClick={onCall}>
                      <AudioWavesIcon />
                      Start call
                    </button>
                  )}
                  <button
                    className='empty-state-btn'
                    onClick={() => onAddTodo?.()}
                  >
                    <PlusAddIcon />
                    Create task
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className='tasks-list'>
              {sectionTab === 'active' &&
                pendingTodos.map((pt) => (
                  <div
                    key={pt.id}
                    className='agent-task-item loading'
                    data-agent-task-id={pt.id}
                  >
                    <div className='indicator indicator-active' />
                    <div className='task-icon'>
                      <div className='task-icon-loading' />
                    </div>
                    <div className='task-main'>
                      <div className='task-description'>{pt.description}</div>
                      <div className='task-status'>
                        <TodoSpinnerIcon className='step-icon spinner' />
                        <span>Reading…</span>
                      </div>
                    </div>
                  </div>
                ))}
              {displayedTodos.map((todo) => renderItem(todo))}
            </div>
          )}
        </div>

        {agentId && (
          <NewThreadModal
            isOpen={createModalType !== null}
            onClose={() => setCreateModalType(null)}
            agentId={agentId}
            agentName={agentNickname}
            agentImageUrl={agentImageUrl}
            agentType={agentType}
            creationType={createModalType || undefined}
          />
        )}

        {isMobile &&
          !hideFab &&
          createPortal(
            <>
              <button
                className={`mobile-fab${fabOpen ? ' open' : ''}${dailyStandupId && !fabOpen ? ' pulsing' : ''}`}
                onClick={() => setFabOpen(!fabOpen)}
                aria-label={fabOpen ? 'Close' : 'Actions'}
              >
                <span className='mobile-fab-inner'>
                  <svg width='24' height='24' viewBox='0 0 24 24' fill='none'>
                    <path
                      d='M12 5V19M5 12H19'
                      stroke='#F3F3F5'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </span>
              </button>
              {fabOpen && (
                <div
                  className='mobile-fab-overlay'
                  onClick={() => setFabOpen(false)}
                >
                  <div
                    className='mobile-fab-menu'
                    onClick={(e) => e.stopPropagation()}
                  >
                    {dailyStandupId && onRundown && (
                      <div
                        className='fab-action'
                        style={{ animationDelay: '0ms' }}
                      >
                        <div className='fab-action-label-group'>
                          <span className='fab-action-label'>
                            Rundown Ready!
                          </span>
                          {countdown && (
                            <span className='fab-action-sublabel'>
                              You&rsquo;ve got {countdown}
                            </span>
                          )}
                        </div>
                        <button
                          className='fab-action-btn rundown'
                          onClick={() => {
                            setFabOpen(false);
                            onRundown();
                          }}
                        >
                          <svg
                            width='24'
                            height='24'
                            viewBox='0 0 24 24'
                            fill='none'
                          >
                            <path
                              d='M20.9996 11.5C20.9996 16.1944 17.194 20 12.4996 20C11.4228 20 10.3928 19.7998 9.44478 19.4345C9.27145 19.3678 9.18478 19.3344 9.11586 19.3185C9.04807 19.3029 8.999 19.2963 8.92949 19.2937C8.85881 19.291 8.78127 19.299 8.62619 19.315L3.50517 19.8444C3.01692 19.8948 2.7728 19.9201 2.6288 19.8322C2.50337 19.7557 2.41794 19.6279 2.3952 19.4828C2.36909 19.3161 2.48575 19.1002 2.71906 18.6684L4.35472 15.6408C4.48942 15.3915 4.55677 15.2668 4.58728 15.1469C4.6174 15.0286 4.62469 14.9432 4.61505 14.8214C4.60529 14.6981 4.55119 14.5376 4.443 14.2166C4.15547 13.3636 3.99962 12.45 3.99962 11.5C3.99962 6.80558 7.8052 3 12.4996 3C17.194 3 20.9996 6.80558 20.9996 11.5Z'
                              stroke='#000D26'
                              strokeWidth='2'
                              strokeLinecap='round'
                              strokeLinejoin='round'
                            />
                            <path
                              fillRule='evenodd'
                              clipRule='evenodd'
                              d='M12.4965 8.94925C11.5968 7.9104 10.0965 7.63095 8.96924 8.58223C7.84196 9.5335 7.68326 11.124 8.56851 12.2491C9.11696 12.9461 10.4935 14.2191 11.4616 15.087C11.8172 15.4057 11.995 15.5651 12.2084 15.6293C12.3914 15.6844 12.6017 15.6844 12.7847 15.6293C12.9981 15.5651 13.1759 15.4057 13.5315 15.087C14.4996 14.2191 15.8761 12.9461 16.4246 12.2491C17.3098 11.124 17.1705 9.5235 16.0238 8.58223C14.8772 7.64096 13.3963 7.9104 12.4965 8.94925Z'
                              stroke='#000D26'
                              strokeWidth='2'
                              strokeLinecap='round'
                              strokeLinejoin='round'
                            />
                          </svg>
                        </button>
                      </div>
                    )}
                    {onCall && (
                      <div
                        className='fab-action'
                        style={{
                          animationDelay: dailyStandupId ? '40ms' : '0ms',
                        }}
                      >
                        <span className='fab-action-label'>Call</span>
                        <button
                          className='fab-action-btn'
                          onClick={() => {
                            setFabOpen(false);
                            onCall();
                          }}
                        >
                          <AudioWavesIcon />
                        </button>
                      </div>
                    )}
                    {onAsk && (
                      <div
                        className='fab-action'
                        style={{
                          animationDelay: dailyStandupId ? '80ms' : '40ms',
                        }}
                      >
                        <span className='fab-action-label'>Chat</span>
                        <button
                          className='fab-action-btn'
                          onClick={() => {
                            setFabOpen(false);
                            onAsk();
                          }}
                        >
                          <ChatBubbleIcon />
                        </button>
                      </div>
                    )}
                    <div
                      className='fab-action'
                      style={{
                        animationDelay: dailyStandupId ? '120ms' : '80ms',
                      }}
                    >
                      <span className='fab-action-label'>Task</span>
                      <button
                        className='fab-action-btn primary'
                        onClick={() => {
                          setFabOpen(false);
                          onAddTodo?.();
                        }}
                      >
                        <ClipboardCheckIcon />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>,
            document.body,
          )}

        <DismissTodoModal
          show={!!stopModalTodo}
          todo={stopModalTodo}
          onClose={() => setStopModalTodo(null)}
          onConfirm={(todo) => {
            setStopModalTodo(null);
            handleStopTask(todo);
          }}
        />
      </div>
    );
  },
);

export default AgentTasksList;
